/**
 * Public questionnaire — anyone with the token can view + submit. No login.
 * Path: /q/:token
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

export default function PublicQuestionnaire() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/questionnaires/${token}`)
      .then(({ data }) => setState({ loading: false, ...data }))
      .catch((e) => setState({ loading: false, error: e.response?.data?.detail || "Invalid link" }));
  }, [token]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/questionnaires/${token}/submit`, { answers });
      setDone(true);
      toast.success("Thanks! Your answers were saved.");
      if (data.ai_summary) {
        // Reveal in UI too
      }
    } catch (e) {
      const err = e.response?.data?.detail;
      if (err && typeof err === "object" && err.missing) toast.error(`Please fill: ${err.missing.join(", ")}`);
      else toast.error(String(err || "Submission failed"));
    } finally { setBusy(false); }
  };

  if (state.loading) return <FullPage><Loader2 className="w-6 h-6 animate-spin" /></FullPage>;
  if (state.error) return <FullPage><p className="text-red-600 text-sm">{state.error}</p></FullPage>;
  if (state.already_submitted) return <FullPage>
    <CheckCircle2 className="w-10 h-10 text-green-600 mb-4" />
    <p className="text-lg font-semibold">Already submitted — thank you.</p>
    <p className="text-sm text-muted-foreground">Received {String(state.submitted_at).slice(0, 16).replace('T', ' ')}.</p>
  </FullPage>;
  if (done) return <FullPage>
    <CheckCircle2 className="w-10 h-10 text-green-600 mb-4" />
    <p className="text-lg font-semibold">Submitted — thanks!</p>
    <p className="text-sm text-muted-foreground">You can close this tab. The team will follow up.</p>
  </FullPage>;

  const q = state.questionnaire || {};
  const workspace = state.workspace || {};
  const accent = workspace.brand_color || "#0ea5e9";

  return (
    <div className="min-h-screen bg-secondary/20" data-testid="public-questionnaire">
      <div className="h-1" style={{ background: accent }} />
      <div className="max-w-2xl mx-auto py-10 px-6">
        <div className="flex items-center gap-3 mb-6">
          {workspace.logo_url && <img src={workspace.logo_url} alt={workspace.name} className="h-8" />}
          <div className="text-sm text-muted-foreground">{workspace.name}</div>
        </div>
        <h1 className="text-3xl font-bold mb-2" data-testid="public-questionnaire-title">{q.name}</h1>
        {q.description && <p className="text-sm text-muted-foreground mb-6">{q.description}</p>}
        <div className="space-y-4">
          {(q.fields || []).map((f) => (
            <div key={f.key} data-testid={`public-field-${f.key}`}>
              <Label className="text-sm font-medium">{f.label}{f.required && <span className="text-red-500 ml-1">*</span>}</Label>
              {f.help_text && <p className="text-[11px] text-muted-foreground">{f.help_text}</p>}
              {renderInput(f, answers[f.key], (v) => setAnswers((prev) => ({ ...prev, [f.key]: v })))}
            </div>
          ))}
        </div>
        <Button onClick={submit} disabled={busy} className="rounded-none mt-6 w-full h-11 font-bold uppercase tracking-widest text-xs" data-testid="public-submit-btn">
          {busy ? "Submitting…" : "Submit"}
        </Button>
        <div className="text-[10px] text-muted-foreground text-center mt-4" data-testid="powered-by">
          Powered by{" "}
          <a href="https://acquilix.com" target="_blank" rel="noopener noreferrer"
              className="underline font-semibold hover:text-foreground transition-colors">
            Acquilix<sup className="text-[0.6em] ml-0.5 font-normal">™</sup>
          </a>
        </div>
      </div>
    </div>
  );
}

function renderInput(f, value, onChange) {
  const testid = `public-input-${f.key}`;
  if (f.kind === "long_text") return <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={4} className="rounded-none mt-1" data-testid={testid} />;
  if (f.kind === "single_choice") return (
    <div className="mt-1 space-y-1">
      {(f.options || []).map((o) => (
        <label key={o} className="flex items-center gap-2 text-sm">
          <input type="radio" name={f.key} checked={value === o} onChange={() => onChange(o)} data-testid={`${testid}-${o}`} /> {o}
        </label>
      ))}
    </div>
  );
  if (f.kind === "multi_choice") return (
    <div className="mt-1 space-y-1">
      {(f.options || []).map((o) => (
        <label key={o} className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={(value || []).includes(o)} onChange={(e) => {
            const arr = value || [];
            onChange(e.target.checked ? [...arr, o] : arr.filter((x) => x !== o));
          }} data-testid={`${testid}-${o}`} /> {o}
        </label>
      ))}
    </div>
  );
  if (f.kind === "rating") return (
    <div className="flex gap-1 mt-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" onClick={() => onChange(n)} className={`w-9 h-9 border ${Number(value) === n ? "border-primary bg-primary text-primary-foreground" : "border-border"}`} data-testid={`${testid}-${n}`}>{n}</button>
      ))}
    </div>
  );
  if (f.kind === "date") return <Input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-10 rounded-none mt-1" data-testid={testid} />;
  if (f.kind === "number") return <Input type="number" value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-10 rounded-none mt-1" data-testid={testid} />;
  return <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-10 rounded-none mt-1" data-testid={testid} />;
}

function FullPage({ children }) {
  return <div className="min-h-screen flex flex-col items-center justify-center bg-secondary/20 p-6 text-center">{children}</div>;
}
